import axios, { Method } from "axios";
import { toast } from "react-toastify";

interface ApiResponse<T = any> {
  [x: string]: any;
  status: string;
  message: string;
  data?: T;
}

// Define the service function type
interface AuthApiServiceParams {
  method: Method;
  endpoint: string;
  data?: any;
  headers?: Record<string, string>;
  apiHeaders: {
    token?: string;
    clientSlug?: string;
    locale?: string;
    clientId?: string;
  };
}

const ApiService = async <T = any>({
  method,
  endpoint,
  data = null,
  headers = {},
  apiHeaders,
}: AuthApiServiceParams): Promise<ApiResponse<T>> => {
  const { token, clientSlug, locale, clientId } = apiHeaders;
  // Fall back to the default locale: on a cold load the locale can still be empty,
  // and `/${""}/login` becomes `//login`, which the browser treats as the host
  // "login" (navigating to the unresolvable https://login/).
  const safeLocale = locale || "en";

  if (!token && !endpoint.includes("invitation"))
    window.location.href = `/${safeLocale}/login`;

  const url = process.env.backEndPoint + `${endpoint}`;
  headers.clientId = clientId || "";

  try {
    const response = await axios({
      method,
      url,
      data,
      headers: { ...headers, lang: locale },
    });
    var result = response.data.response;
    if (result.status === "OK") {
      return result;
    } else if (result.status === "FAILED" && result?.pending_action) {
      const actionPath = result.pending_action[0].startsWith("/")
        ? result.pending_action[0].slice(1)
        : result.pending_action[0];
      window.location.href = `/${safeLocale}/${actionPath}`;
      throw "";
    } else {
      toast.error(result.message);
      throw result.message;
    }
  } catch (error: any) {
    const regex = /^4\d{2}$/;

    const statusCode = error?.response?.status;

    if (statusCode == 404) {
      window.location.href = `/${safeLocale}/not-found`;
      throw new Error("Not Found");
    }
    else if (regex.test(statusCode)) {
      if (endpoint.includes("invitation")) {
        if (statusCode === 401) {
          window.location.href = error?.response?.data?.response?.data.url;
          localStorage.setItem(
            "email",
            error?.response?.data?.response?.data?.email
          );
        } else throw new Error(error?.response?.data?.response?.message);
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("persist:root");
        localStorage.removeItem("md5");
        localStorage.removeItem("otp-value");
        localStorage.removeItem("otp-type");
        localStorage.removeItem("email");
        sessionStorage.clear();
        window.location.href = `/${safeLocale}/login`;
      }
    } else throw error?.response?.data?.response ?? error;
  }

  return {
    status: "FAILED",
    message: "An unexpected error occurred",
  };
};

export default ApiService;
